/*
 * Select (Figma 119:44919 closed, 119:44927 open with menu).
 * Wrap: column, gap 8, width from the caller (320px in the Report frame). Label 550 14/20 ss03.
 * Trigger, closed: radius 8, --surface-raised, padding 8px, --field-stroke; content gap 12;
 *   placeholder 400 14/20 --text-weak; caret 16px.
 * Trigger, open: padding 6px 8px 6px 6px, plus "Button Focus" ring (--focus-field); selected
 *   values as small Chips.
 * Menu: 6px below the trigger, radius 8, --surface-raised, --shadow-popout; item padding 6px 8px,
 *   gap 8, radius 8, text 400 14/20 ss03+ss04; highlighted --interaction-hover; selected = 16px check.
 * Keyboard: ↑/↓ move, Enter/Space toggle, Esc closes; click outside closes.
 * Option count (not in Figma; DESIGN-SPEC §6): item text style in --text-weak, before the check.
 * Touch (not in Figma): trigger and options 40px high on a coarse pointer; never wider than its parent.
 * Long lists (not in Figma; SEARCH_FROM or more options, e.g. brands): a search field heads the
 * menu (focused on open; ↑/↓, Enter and Esc work from it) and the options scroll under it, at most
 * 320px. The closed trigger shows up to MAX_CHIPS chips and "+N" for the rest.
 * Motion (not in Figma): the menu grows from the trigger's top edge and folds back into it
 * (.motion-pop, usePresence); an option darkens while pressed.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Chip } from './Chip'
import { cx } from './cx'
import { Icon } from './Icon'
import { caretDown16, caretDown16Open, check16Menu } from './icons'
import { usePresence } from './usePresence'

const SEARCH_FROM = 9
const MAX_CHIPS = 3

/** Case- and accent-insensitive text for matching ("Nägel" is found by "nagel"). */
function fold(text: string): string {
    return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

export interface SelectOption {
    value: string
    label: string
    /** Shown after the label in the menu only (e.g. how many items the option matches). */
    count?: number
}

export function MultiSelect({
    label,
    placeholder,
    options,
    value,
    onChange,
    width = 320,
    removeLabel = (option: string) => option,
    searchLabel = 'Search',
    noMatchesLabel = 'No matches',
}: {
    label: string
    placeholder: string
    options: SelectOption[]
    value: string[]
    onChange: (value: string[]) => void
    width?: number
    removeLabel?: (optionLabel: string) => string
    /** Placeholder and name of the search field of a long list (SDK 1.6). */
    searchLabel?: string
    /** Shown when the search matches no option (SDK 1.6). */
    noMatchesLabel?: string
}) {
    const id = useId()
    const [open, setOpen] = useState(false)
    const presence = usePresence(open)
    const [highlight, setHighlight] = useState(0)
    const [query, setQuery] = useState('')
    const root = useRef<HTMLDivElement>(null)
    const trigger = useRef<HTMLDivElement>(null)
    const search = useRef<HTMLInputElement>(null)
    const list = useRef<HTMLUListElement>(null)
    const selected = options.filter((o) => value.includes(o.value))
    const searchable = options.length >= SEARCH_FROM
    const shown = searchable && query.trim() ? options.filter((o) => fold(o.label).includes(fold(query.trim()))) : options

    // The menu opens at the top, with an empty search.
    const setMenu = (next: boolean) => {
        if (next) {
            setQuery('')
            setHighlight(0)
        }
        setOpen(next)
    }

    // A long list's search field takes the focus when the menu opens.
    useEffect(() => {
        if (open && searchable) search.current?.focus()
    }, [open, searchable])

    // Keep the highlighted option in view while ↑/↓ move through a scrolling list.
    useEffect(() => {
        if (open) list.current?.children[highlight]?.scrollIntoView?.({ block: 'nearest' })
    }, [open, highlight])

    useEffect(() => {
        if (!open) return
        const onPointer = (event: PointerEvent) => {
            if (!root.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', onPointer)
        return () => document.removeEventListener('pointerdown', onPointer)
    }, [open])

    const toggle = (optionValue: string) => onChange(value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue])

    const close = () => {
        setOpen(false)
        trigger.current?.focus()
    }

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            if (open) close()
            return
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            if (!open) {
                setMenu(true)
                return
            }
            if (shown.length === 0) return
            const step = event.key === 'ArrowDown' ? 1 : -1
            setHighlight((h) => (h + step + shown.length) % shown.length)
        } else if ((event.key === 'Enter' || (event.key === ' ' && event.target !== search.current)) && open) {
            event.preventDefault()
            const option = shown[highlight]
            if (option) toggle(option.value)
        } else if (event.key === 'Tab' && open) {
            setOpen(false)
        }
    }

    return (
        <div ref={root} className="flex max-w-full shrink-0 flex-col items-start gap-[8px]" style={{ width }}>
            <span id={`${id}-label`} className="ss03 flex items-center whitespace-nowrap text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">
                {label}
            </span>
            <div className="relative w-full">
                <div
                    ref={trigger}
                    role="combobox"
                    tabIndex={0}
                    aria-labelledby={`${id}-label`}
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    aria-controls={`${id}-menu`}
                    onClick={() => setMenu(!open)}
                    onKeyDown={onKeyDown}
                    className={cx(
                        'flex w-full cursor-pointer items-center gap-[12px] overflow-clip rounded-[8px] bg-[var(--surface-raised)] outline-none',
                        open
                            ? 'py-[6px] pr-[8px] pl-[6px] shadow-[var(--field-stroke),var(--focus-field)] pointer-coarse:py-[8px]'
                            : 'p-[8px] shadow-[var(--field-stroke)] focus-visible:shadow-[var(--field-stroke),var(--focus-field)] pointer-coarse:py-[10px]',
                    )}
                >
                    <div className="flex min-w-px flex-1 flex-wrap items-center gap-[4px]">
                        {selected.length === 0 ? (
                            <span className="min-w-px flex-1 text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{placeholder}</span>
                        ) : (
                            <>
                                {selected.slice(0, MAX_CHIPS).map((option) => (
                                    <span key={option.value} className="max-w-full min-w-0" onClick={(e) => e.stopPropagation()}>
                                        <Chip label={option.label} onRemove={() => toggle(option.value)} removeLabel={removeLabel(option.label)} />
                                    </span>
                                ))}
                                {selected.length > MAX_CHIPS && (
                                    <span className="ss03-ss04 px-[4px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">+{selected.length - MAX_CHIPS}</span>
                                )}
                            </>
                        )}
                    </div>
                    <Icon src={open ? caretDown16Open : caretDown16} size={16} />
                </div>
                {presence.mounted && (
                    <div data-state={presence.state} inert={!open || undefined} style={{ transformOrigin: 'top center' }}
                        className="motion-pop absolute top-[calc(100%+6px)] right-0 left-0 z-20 flex flex-col overflow-clip rounded-[8px] bg-[var(--surface-raised)] shadow-[var(--shadow-popout)]">
                        {searchable && (
                            <div className="border-b border-solid border-[var(--border-default)] p-[6px]">
                                <input
                                    ref={search}
                                    type="search"
                                    value={query}
                                    placeholder={searchLabel}
                                    aria-label={searchLabel}
                                    aria-controls={`${id}-menu`}
                                    aria-activedescendant={shown[highlight] ? `${id}-option-${highlight}` : undefined}
                                    autoComplete="off"
                                    onChange={(e) => {
                                        setQuery(e.target.value)
                                        setHighlight(0)
                                    }}
                                    onKeyDown={onKeyDown}
                                    className="w-full rounded-[8px] bg-[var(--surface-raised)] px-[8px] py-[6px] text-[14px] leading-[20px] font-normal text-[var(--text-default)] shadow-[var(--field-stroke)] outline-none placeholder:text-[var(--text-weak)] focus-visible:shadow-[var(--field-stroke),var(--focus-field)] pointer-coarse:py-[10px]"
                                />
                            </div>
                        )}
                        {shown.length === 0 && (
                            <p role="status" className="m-0 px-[8px] py-[6px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
                                {noMatchesLabel}
                            </p>
                        )}
                        <ul
                            ref={list}
                            id={`${id}-menu`}
                            role="listbox"
                            aria-multiselectable
                            aria-labelledby={`${id}-label`}
                            className={cx('m-0 flex list-none flex-col items-start p-0', searchable && 'max-h-[320px] overflow-y-auto overscroll-contain')}
                        >
                            {shown.map((option, index) => {
                                const isSelected = value.includes(option.value)
                                return (
                                    <li
                                        key={option.value}
                                        id={`${id}-option-${index}`}
                                        role="option"
                                        aria-selected={isSelected}
                                        onMouseEnter={() => setHighlight(index)}
                                        onClick={() => toggle(option.value)}
                                        className={cx(
                                            'flex w-full cursor-pointer items-center gap-[8px] rounded-[8px] px-[8px] py-[6px] pointer-coarse:py-[10px] active:bg-[var(--interaction-active)]',
                                            index === highlight ? 'bg-[var(--interaction-hover)]' : 'bg-[var(--surface-raised)]',
                                        )}
                                    >
                                        <span className="ss03-ss04 min-w-px flex-1 text-[14px] leading-[20px] font-normal text-[var(--text-default)]">{option.label}</span>
                                        {option.count !== undefined && <span className="ss03-ss04 text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{option.count}</span>}
                                        {isSelected && <Icon src={check16Menu} size={16} />}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    )
}
